import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../api/client";
import { OpenRouterPicker } from "./OpenRouterPicker";
import {
  DEFAULT_VALIDATOR_CONFIG,
  ValidatorConfigRow,
  resolveValidatorModel,
  type ValidatorConfigState,
} from "./ValidatorConfigRow";
import type {
  AdminJob,
  CompareModelOutcome,
  CompareModelId,
  CompareRunDetail,
  CompareRunSummary,
  EnqueueCompareInput,
  GenerateCardType,
  GenerateFaction,
  GenerateRarity,
} from "../../api/client";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";

// Side-by-side LLM comparison admin tool.
//
// One spec, N models, byte-identical prompts. Outputs are throwaway —
// they live under `card-engine/comparisons/<run_id>/` (gitignored) and
// never enter the live pool.
//
// The picker is fixed to a small known list of model ids; we don't
// expose a "custom" slot because the goal is comparing the four
// production-relevant models, not arbitrary experimentation.

const MODEL_LABELS: Record<CompareModelId, string> = {
  "claude-opus-4-7": "Opus 4.7",
  "claude-opus-4-6": "Opus 4.6",
  "claude-sonnet-4-6": "Sonnet 4.6",
  "claude-haiku-4-5-20251001": "Haiku 4.5",
};

const FACTIONS: ReadonlyArray<GenerateFaction> = [
  "humans",
  "goblins",
  "dwarves",
  "orcs",
  "elves",
];
const RARITIES: ReadonlyArray<GenerateRarity> = [
  "common",
  "uncommon",
  "rare",
  "legendary",
];
const CARD_TYPES: ReadonlyArray<GenerateCardType> = [
  "creature",
  "spell",
  "enchantment",
  "trap",
];

type AnyOrSpecific<T extends string> = "any" | T;

interface FormState {
  models: Record<CompareModelId, boolean>;
  /** OpenRouter model ids picked from the autocomplete catalog. Each
   *  carries a provider prefix (`openai/gpt-5`, `anthropic/claude-
   *  opus-4-7`); the Python tool routes any slash-bearing id through
   *  OpenRouter. */
  openrouterModels: string[];
  faction: AnyOrSpecific<GenerateFaction>;
  rarity: AnyOrSpecific<GenerateRarity>;
  cardType: AnyOrSpecific<GenerateCardType>;
  cost: string;
  typicality: string;
  seed: string;
  seedWords: string;
  thinking: boolean;
  /** Rules-validator second-pass configuration. Defaults come from the
   *  shared admin validator row used by both comparison tools. */
  validator: ValidatorConfigState;
}

const DEFAULT_FORM: FormState = {
  models: {
    "claude-opus-4-7": true,
    "claude-opus-4-6": false,
    "claude-sonnet-4-6": false,
    "claude-haiku-4-5-20251001": true,
  },
  openrouterModels: [],
  faction: "any",
  rarity: "any",
  cardType: "any",
  cost: "",
  typicality: "",
  seed: "",
  seedWords: "",
  thinking: false,
  validator: DEFAULT_VALIDATOR_CONFIG,
};

export function ModelCompareTool() {
  const theme = pixelTheme;

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<CompareRunSummary[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<CompareRunDetail | null>(null);
  const [activeRunError, setActiveRunError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<AdminJob[]>([]);

  const refreshRuns = useCallback(async () => {
    try {
      const { runs } = await api.listCompareRuns();
      // The same comparisons root holds both `compare_models` and
      // `compare_prompts` runs now. Filter to this tool's kind so the
      // list doesn't bleed prompt-version runs into the model-compare
      // view (and vice-versa). Legacy runs default to compare_models
      // server-side.
      setRuns(runs.filter((r) => r.kind === "compare_models"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const refreshJobs = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      setJobs(jobs.filter((j) => j.kind === "compare_models"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
    void refreshJobs();
    // Poll while a job is in flight so a freshly-finished run shows up
    // in the list without needing a manual refresh.
    const id = setInterval(() => {
      void refreshRuns();
      void refreshJobs();
    }, 3000);
    return () => clearInterval(id);
  }, [refreshRuns, refreshJobs]);

  // Load the selected run's full payload on demand.
  useEffect(() => {
    if (!activeRunId) {
      setActiveRun(null);
      setActiveRunError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.getCompareRun(activeRunId);
        if (!cancelled) {
          setActiveRun(detail);
          setActiveRunError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setActiveRunError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeRunId]);

  const selectedModels = useMemo(
    () =>
      (Object.entries(form.models) as Array<[CompareModelId, boolean]>)
        .filter(([, on]) => on)
        .map(([id]) => id),
    [form.models],
  );

  // Merge the checked Claude Code presets with any OpenRouter chips
  // the user picked. The Python side routes by model-id shape (`/` =
  // OpenRouter), so the wire format is just one flat list.
  const allModels = useMemo(
    () => [...selectedModels, ...form.openrouterModels],
    [selectedModels, form.openrouterModels],
  );

  async function onRun() {
    setError(null);
    if (allModels.length === 0) {
      setError("Pick at least one model.");
      return;
    }
    const validatorModel = resolveValidatorModel(form.validator);
    if (validatorModel == null) {
      setError("Pick a validator model.");
      return;
    }
    const input: EnqueueCompareInput = {
      models: allModels,
      thinking: form.thinking,
      validator_model: validatorModel,
      validator_thinking: form.validator.thinking,
    };
    if (form.faction !== "any") input.faction = form.faction;
    if (form.rarity !== "any") input.rarity = form.rarity;
    if (form.cardType !== "any") input.card_type = form.cardType;
    const cost = parseIntOrNull(form.cost);
    if (cost !== null) input.cost = cost;
    const typ = parseIntOrNull(form.typicality);
    if (typ !== null) input.typicality = typ;
    const seed = parseIntOrNull(form.seed);
    if (seed !== null) input.seed = seed;
    const words = form.seedWords
      .split(/\s+/)
      .map((w) => w.trim())
      .filter((w) => w.length > 0);
    if (words.length > 0) input.seed_words = words;

    setBusy(true);
    try {
      await api.enqueueCompareModels(input);
      await refreshJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 12,
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SpecForm
          theme={theme}
          form={form}
          setForm={setForm}
          totalModels={allModels.length}
          onRun={onRun}
          busy={busy}
        />
        {error && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${theme.accent}`,
              color: theme.accent,
              background: theme.panel,
            }}
          >
            {error}
          </div>
        )}
        <JobsStrip jobs={jobs} theme={theme} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(260px, 320px) 1fr",
          gap: 12,
          minHeight: 0,
        }}
      >
        <RunsList
          runs={runs}
          activeRunId={activeRunId}
          onSelect={setActiveRunId}
          theme={theme}
        />
        <RunViewer
          run={activeRun}
          error={activeRunError}
          loading={Boolean(activeRunId) && !activeRun && !activeRunError}
          theme={theme}
        />
      </div>
    </div>
  );
}

function parseIntOrNull(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
}

function SpecForm({
  theme,
  form,
  setForm,
  totalModels,
  onRun,
  busy,
}: {
  theme: typeof pixelTheme;
  form: FormState;
  setForm: (f: FormState) => void;
  totalModels: number;
  onRun: () => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gap: 10,
        padding: 12,
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
      }}
    >
      <Row>
        <FieldLabel theme={theme}>MODELS</FieldLabel>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {api.COMPARE_MODEL_IDS.map((id) => (
            <label
              key={id}
              style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={form.models[id]}
                onChange={(e) =>
                  setForm({
                    ...form,
                    models: { ...form.models, [id]: e.target.checked },
                  })
                }
              />
              <span style={{ color: theme.ink, fontSize: 13 }}>
                {MODEL_LABELS[id]}
              </span>
            </label>
          ))}
        </div>
      </Row>
      <Row>
        <FieldLabel theme={theme}>OPENROUTER</FieldLabel>
        <div style={{ flex: 1, minWidth: 280 }}>
          <OpenRouterPicker
            selected={form.openrouterModels}
            onChange={(next) => setForm({ ...form, openrouterModels: next })}
            disabled={busy}
          />
        </div>
      </Row>
      <Row>
        <FieldLabel theme={theme}>VALIDATOR</FieldLabel>
        <ValidatorConfigRow
          state={form.validator}
          onChange={(next) => setForm({ ...form, validator: next })}
          disabled={busy}
        />
      </Row>
      <Row>
        <FieldLabel theme={theme}>SPEC</FieldLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <SelectField
            theme={theme}
            label="faction"
            value={form.faction}
            onChange={(v) =>
              setForm({ ...form, faction: v as AnyOrSpecific<GenerateFaction> })
            }
            options={["any", ...FACTIONS]}
          />
          <SelectField
            theme={theme}
            label="rarity"
            value={form.rarity}
            onChange={(v) =>
              setForm({ ...form, rarity: v as AnyOrSpecific<GenerateRarity> })
            }
            options={["any", ...RARITIES]}
          />
          <SelectField
            theme={theme}
            label="type"
            value={form.cardType}
            onChange={(v) =>
              setForm({ ...form, cardType: v as AnyOrSpecific<GenerateCardType> })
            }
            options={["any", ...CARD_TYPES]}
          />
          <NumberField
            theme={theme}
            label="cost"
            value={form.cost}
            onChange={(v) => setForm({ ...form, cost: v })}
            placeholder="any"
            width={70}
          />
          <NumberField
            theme={theme}
            label="typicality (1-10)"
            value={form.typicality}
            onChange={(v) => setForm({ ...form, typicality: v })}
            placeholder="any"
            width={110}
          />
          <NumberField
            theme={theme}
            label="seed"
            value={form.seed}
            onChange={(v) => setForm({ ...form, seed: v })}
            placeholder="random"
            width={100}
          />
        </div>
      </Row>
      <Row>
        <FieldLabel theme={theme}>SEED WORDS</FieldLabel>
        <input
          type="text"
          value={form.seedWords}
          onChange={(e) => setForm({ ...form, seedWords: e.target.value })}
          placeholder="space-separated; leave blank to draw from the pool"
          style={{
            ...inputBase(theme),
            flex: 1,
            minWidth: 240,
          }}
        />
      </Row>
      <Row>
        <FieldLabel theme={theme}> </FieldLabel>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label
            style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={form.thinking}
              onChange={(e) => setForm({ ...form, thinking: e.target.checked })}
            />
            <span style={{ color: theme.ink, fontSize: 13 }}>extended thinking</span>
          </label>
          <button
            onClick={onRun}
            disabled={busy || totalModels === 0}
            style={{
              padding: "6px 12px",
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              background: theme.accent,
              color: theme.bgDeep,
              border: `2px solid ${theme.accent}`,
              cursor: busy || totalModels === 0 ? "not-allowed" : "pointer",
              opacity: totalModels === 0 ? 0.5 : 1,
            }}
          >
            {busy ? "ENQUEUING…" : `RUN ${totalModels} MODEL${totalModels === 1 ? "" : "S"}`}
          </button>
          <span style={{ color: theme.inkFaint, fontSize: 12 }}>
            Each model gets the byte-identical prompt; only the model id varies.
          </span>
        </div>
      </Row>
    </div>
  );
}

function inputBase(theme: typeof pixelTheme): React.CSSProperties {
  return {
    padding: "6px 8px",
    background: theme.bgDeep,
    color: theme.ink,
    border: `1px solid ${theme.panelEdge}`,
    fontFamily: "monospace",
    fontSize: 12,
  };
}

function SelectField({
  theme,
  label,
  value,
  onChange,
  options,
}: {
  theme: typeof pixelTheme;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<string>;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, color: theme.inkFaint, letterSpacing: theme.letterSpacing }}>
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "6px 8px",
          background: theme.panel,
          color: theme.ink,
          border: `1px solid ${theme.panelEdge}`,
          fontFamily: theme.fontDisplay,
          fontSize: 11,
        }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberField({
  theme,
  label,
  value,
  onChange,
  placeholder,
  width,
}: {
  theme: typeof pixelTheme;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, color: theme.inkFaint, letterSpacing: theme.letterSpacing }}>
        {label}
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputBase(theme), width }}
      />
    </label>
  );
}

function FieldLabel({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        letterSpacing: theme.letterSpacing,
        color: theme.inkDim,
        minWidth: 110,
        paddingTop: 6,
      }}
    >
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>{children}</div>
  );
}

function JobsStrip({
  jobs,
  theme,
}: {
  jobs: AdminJob[];
  theme: typeof pixelTheme;
}) {
  if (jobs.length === 0) return null;
  // Show only the most recent 3 — older ones are scrollable in the
  // global jobs view; this strip is just an "is anything in flight"
  // signal for the active session.
  const recent = jobs.slice(0, 3);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: theme.inkDim,
          letterSpacing: theme.letterSpacing,
        }}
      >
        RECENT JOBS
      </div>
      {recent.map((j) => (
        <div
          key={j.id}
          style={{
            padding: "4px 8px",
            border: `1px solid ${theme.panelEdge}`,
            background: theme.panel,
            color: theme.ink,
            fontSize: 12,
            display: "flex",
            gap: 8,
          }}
        >
          <span style={{ color: jobStatusColor(theme, j.status) }}>{j.status}</span>
          <span style={{ color: theme.inkFaint }}>{j.id}</span>
          {j.error && <span style={{ color: theme.accent }}>· {j.error}</span>}
        </div>
      ))}
    </div>
  );
}

function jobStatusColor(theme: typeof pixelTheme, status: AdminJob["status"]): string {
  switch (status) {
    case "queued":
      return theme.inkFaint;
    case "running":
      return theme.accent;
    case "succeeded":
      return theme.ink;
    case "failed":
      return theme.accent;
  }
}

function RunsList({
  runs,
  activeRunId,
  onSelect,
  theme,
}: {
  runs: CompareRunSummary[];
  activeRunId: string | null;
  onSelect: (id: string | null) => void;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: theme.inkDim,
          letterSpacing: theme.letterSpacing,
        }}
      >
        RUNS
      </div>
      {runs.length === 0 ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>No runs yet.</div>
      ) : (
        runs.map((r) => {
          const active = r.run_id === activeRunId;
          // Sum costs across the models that produced any. `null`
          // entries (failed-before-ResultMessage runs and unpriced
          // models) just don't contribute, which is the same way the
          // detail view treats them.
          const totalCost = r.models.reduce<number>((acc, m) => {
            return acc + (typeof m.cost_usd === "number" ? m.cost_usd : 0);
          }, 0);
          return (
            <button
              key={r.run_id}
              onClick={() => onSelect(active ? null : r.run_id)}
              style={{
                textAlign: "left",
                padding: 8,
                background: active ? theme.accent : theme.panel,
                color: active ? E32.parchment : theme.ink,
                border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
                cursor: "pointer",
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.run_id}
              </div>
              <div style={{ fontSize: 11, color: active ? E32.parchment : theme.inkFaint }}>
                {r.spec.faction_id} · {r.spec.rarity} · {r.spec.card_type} · cost {r.spec.cost}
              </div>
              <div style={{ fontSize: 11, color: active ? E32.parchment : theme.inkFaint }}>
                {r.models.length} model{r.models.length === 1 ? "" : "s"} ·{" "}
                {fmtCreatedAt(r.created_at)}
                {totalCost > 0 && <> · {formatCompareUsd(totalCost)}</>}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

function fmtCreatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function RunViewer({
  run,
  error,
  loading,
  theme,
}: {
  run: CompareRunDetail | null;
  error: string | null;
  loading: boolean;
  theme: typeof pixelTheme;
}) {
  if (error) {
    return (
      <div
        style={{
          padding: 12,
          border: `2px solid ${theme.accent}`,
          color: theme.accent,
          background: theme.panel,
        }}
      >
        {error}
      </div>
    );
  }
  if (loading) {
    return <div style={{ color: theme.inkFaint }}>Loading run…</div>;
  }
  if (!run) {
    return (
      <div
        style={{
          padding: 12,
          border: `2px solid ${theme.panelEdge}`,
          background: theme.panel,
          color: theme.inkFaint,
        }}
      >
        Pick a run to see each model's output side-by-side.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 8,
        minHeight: 0,
      }}
    >
      <SpecBanner spec={run.spec} runId={run.run_id} theme={theme} />
      <div
        style={{
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: "minmax(320px, 1fr)",
          gap: 12,
          overflowX: "auto",
          minHeight: 0,
        }}
      >
        {run.models.map((m) => (
          <ModelColumn key={m.slug} outcome={m} theme={theme} />
        ))}
      </div>
    </div>
  );
}

function SpecBanner({
  spec,
  runId,
  theme,
}: {
  spec: CompareRunDetail["spec"];
  runId: string;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        padding: 8,
        background: theme.panel,
        border: `1px solid ${theme.panelEdge}`,
        color: theme.ink,
        fontSize: 12,
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontFamily: "monospace" }}>{runId}</span>
      <span>
        {spec.faction_id} · {spec.rarity} · {spec.card_type} · cost {spec.cost} ·
        typicality {spec.typicality}
      </span>
      <span style={{ color: theme.inkFaint }}>
        seeds: {spec.seed_words.join(", ") || "—"}
      </span>
    </div>
  );
}

function ModelColumn({
  outcome,
  theme,
}: {
  outcome: CompareModelOutcome;
  theme: typeof pixelTheme;
}) {
  const card = outcome.card_json;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 10,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          {outcome.model}
        </div>
        <ValidationBadge outcome={outcome} theme={theme} />
      </div>
      <div style={{ color: theme.inkFaint, fontSize: 11 }}>
        llm {outcome.timings.llm.toFixed(1)}s · validation{" "}
        {outcome.timings.validation.toFixed(1)}s
      </div>
      {(outcome.usage || outcome.cost_usd != null) && (
        <CostUsageLine outcome={outcome} theme={theme} />
      )}

      {outcome.error ? (
        <div
          style={{
            padding: 8,
            background: theme.bgDeep,
            border: `1px solid ${theme.accent}`,
            color: theme.accent,
            fontSize: 12,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          llm error: {outcome.error}
        </div>
      ) : outcome.parse_error ? (
        <div
          style={{
            padding: 8,
            background: theme.bgDeep,
            border: `1px solid ${theme.accent}`,
            color: theme.accent,
            fontSize: 12,
            fontFamily: "monospace",
          }}
        >
          parse error: {outcome.parse_error}
        </div>
      ) : (
        card && <CardJsonView card={card} theme={theme} />
      )}

      {outcome.card_lua !== null && (
        <div>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              color: theme.inkDim,
              letterSpacing: theme.letterSpacing,
              marginBottom: 4,
            }}
          >
            CARD.LUA
          </div>
          <pre
            style={{
              margin: 0,
              padding: 8,
              background: theme.bgDeep,
              color: theme.ink,
              border: `1px solid ${theme.panelEdge}`,
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {outcome.card_lua}
          </pre>
        </div>
      )}

    </div>
  );
}

function CostUsageLine({
  outcome,
  theme,
}: {
  outcome: CompareModelOutcome;
  theme: typeof pixelTheme;
}) {
  const u = outcome.usage;
  const inT = u?.input_tokens ?? 0;
  const outT = u?.output_tokens ?? 0;
  const crT = u?.cache_read_input_tokens ?? 0;
  const cwT = u?.cache_creation_input_tokens ?? 0;
  const denom = inT + crT + cwT;
  const hit = denom > 0 ? (crT / denom) * 100 : null;
  const cost = outcome.cost_usd;
  return (
    <div style={{ color: theme.inkFaint, fontSize: 11, lineHeight: 1.4 }}>
      {u && (
        <div>
          in <strong style={{ color: theme.ink }}>{inT.toLocaleString()}</strong>
          {" · "}
          out <strong style={{ color: theme.ink }}>{outT.toLocaleString()}</strong>
          {" · "}
          cache_read <strong style={{ color: theme.ink }}>{crT.toLocaleString()}</strong>
          {" · "}
          cache_write <strong style={{ color: theme.ink }}>{cwT.toLocaleString()}</strong>
          {hit != null && (
            <>
              {" · "}
              hit <strong style={{ color: theme.ink }}>{hit.toFixed(1)}%</strong>
            </>
          )}
        </div>
      )}
      <div>
        cost{" "}
        <strong style={{ color: theme.ink }}>
          {cost != null ? formatCompareUsd(cost) : "—"}
        </strong>
        {cost == null && u && (
          <span style={{ color: theme.inkFaint }}> (model not in rate table)</span>
        )}
      </div>
    </div>
  );
}

function formatCompareUsd(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  if (Math.abs(amount) >= 1) return `$${amount.toFixed(2)}`;
  return `$${amount.toFixed(4)}`;
}

function ValidationBadge({
  outcome,
  theme,
}: {
  outcome: CompareModelOutcome;
  theme: typeof pixelTheme;
}) {
  if (outcome.error) {
    return (
      <span
        title={outcome.error}
        style={{
          fontSize: 10,
          padding: "2px 6px",
          background: theme.accent,
          color: theme.bgDeep,
          letterSpacing: theme.letterSpacing,
          fontFamily: theme.fontDisplay,
        }}
      >
        ERROR ✗
      </span>
    );
  }
  if (outcome.parse_error) {
    return (
      <span
        title={outcome.parse_error}
        style={{
          fontSize: 10,
          padding: "2px 6px",
          background: theme.accent,
          color: theme.bgDeep,
          letterSpacing: theme.letterSpacing,
          fontFamily: theme.fontDisplay,
        }}
      >
        PARSE ✗
      </span>
    );
  }
  if (outcome.validation.ok === true) {
    // The Sonnet rules-vs-lua pass is a *stronger* signal than the
    // engine validator: the engine only checks that the script
    // *runs*, while Sonnet checks that it actually implements the
    // English rules text. A "VALID" engine pass that the rules
    // validator rejected is exactly the case worth flagging — show
    // INVALID RULES in place of VALID.
    if (outcome.rules_validation && outcome.rules_validation.ok === false) {
      return (
        <span
          title={outcome.rules_validation.reason || "rules text and card.lua do not match"}
          style={{
            fontSize: 10,
            padding: "2px 6px",
            background: theme.accent,
            color: theme.bgDeep,
            letterSpacing: theme.letterSpacing,
            fontFamily: theme.fontDisplay,
          }}
        >
          INVALID RULES ✗
        </span>
      );
    }
    return (
      <span
        style={{
          fontSize: 10,
          padding: "2px 6px",
          background: theme.bgDeep,
          color: theme.ink,
          border: `1px solid ${theme.panelEdge}`,
          letterSpacing: theme.letterSpacing,
          fontFamily: theme.fontDisplay,
        }}
      >
        VALID ✓
      </span>
    );
  }
  if (outcome.validation.ok === false) {
    const tag = outcome.validation.error_kind || "FAIL";
    return (
      <span
        title={outcome.validation.error_message || ""}
        style={{
          fontSize: 10,
          padding: "2px 6px",
          background: theme.accent,
          color: theme.bgDeep,
          letterSpacing: theme.letterSpacing,
          fontFamily: theme.fontDisplay,
        }}
      >
        {tag.toUpperCase()} ✗
      </span>
    );
  }
  return (
    <span style={{ fontSize: 10, color: theme.inkFaint }}>—</span>
  );
}

function CardJsonView({
  card,
  theme,
}: {
  card: Record<string, unknown>;
  theme: typeof pixelTheme;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: theme.inkDim,
          letterSpacing: theme.letterSpacing,
          marginBottom: 4,
        }}
      >
        CARD.JSON
      </div>
      <pre
        style={{
          margin: 0,
          padding: 8,
          background: theme.bgDeep,
          color: theme.ink,
          border: `1px solid ${theme.panelEdge}`,
          fontFamily: "monospace",
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(card, null, 2)}
      </pre>
    </div>
  );
}
